/** The conversion view: footfall (entries per branch per day) joined with
 *  the branch's uploaded POS sales. Pure functions over the two API series;
 *  the dashboard slices them per range and per branch.
 *
 *  Conversion is **store transactions ÷ entries**, and only over (branch,
 *  day) pairs that have both — a day with footfall but no sales on file, or
 *  sales but no processed footage, is left out of the rate rather than
 *  dragging it to 0 or ∞. Delivery and call-centre orders are not
 *  conversions (nobody walked in) and returns are not sales. The rate is
 *  capped at 100%: more transactions than counted entries (a missed
 *  camera, a shared basket) is a data gap, not a 130% conversion. */
import type { BranchDailyPoint, SalesDailyPoint } from "../types";
import { weekdayOf } from "./format";

const key = (branchId: string, day: string) => `${branchId}|${day}`;

/** Transactions ÷ entries, never above 1; null without entries. */
export function conversionRate(transactions: number, entries: number): number | null {
  return entries > 0 ? Math.min(1, transactions / entries) : null;
}

export interface DayRow {
  day: string;
  /** Null when no selected branch has footfall that day (a gap, not zero). */
  entries: number | null;
  exits: number | null;
  /** Store (walk-in) transactions; null when no sales are on file that day. */
  transactions: number | null;
  /** Net sales that day, all channels, after returns. */
  net: number | null;
  /** Store transactions ÷ entries over the branches with both. */
  conversion: number | null;
}

/** One row per day, summed over `branchIds` (null = every branch present in
 *  either series). Days a series has nothing for stay null so a line breaks
 *  instead of dropping to the axis. */
export function joinDays(
  days: string[],
  footfall: BranchDailyPoint[],
  sales: SalesDailyPoint[],
  branchIds: string[] | null,
): DayRow[] {
  const ids = branchIds ?? [
    ...new Set([...footfall.map((r) => r.branch_id), ...sales.map((r) => r.branch_id)]),
  ];
  const foot = new Map(footfall.map((r) => [key(r.branch_id, r.day), r]));
  const sold = new Map(sales.map((r) => [key(r.branch_id, r.day), r]));
  return days.map((day) => {
    let entries: number | null = null;
    let exits: number | null = null;
    let transactions: number | null = null;
    let net: number | null = null;
    let matchedEntries = 0;
    let matchedTransactions = 0;
    let matched = false;
    for (const id of ids) {
      const f = foot.get(key(id, day));
      const s = sold.get(key(id, day));
      if (f) {
        entries = (entries ?? 0) + f.entries;
        exits = (exits ?? 0) + f.exits;
      }
      if (s) {
        transactions = (transactions ?? 0) + s.store_transactions;
        net = (net ?? 0) + s.net;
      }
      if (f && s && f.entries > 0) {
        matched = true;
        matchedEntries += f.entries;
        matchedTransactions += s.store_transactions;
      }
    }
    return {
      day,
      entries,
      exits,
      transactions,
      net,
      conversion: matched ? conversionRate(matchedTransactions, matchedEntries) : null,
    };
  });
}

export interface RangeMetrics {
  entries: number;
  exits: number;
  /** Days with footfall / with sales / with both (entries > 0). */
  footfallDays: number;
  salesDays: number;
  matchedDays: number;
  matchedEntries: number;
  matchedTransactions: number;
  matchedStoreNet: number;
  conversion: number | null;
  storeTransactions: number;
  storeNet: number;
  deliveryTransactions: number;
  deliveryNet: number;
  returnTransactions: number;
  returnNet: number;
  discount: number;
  /** Net after returns, all channels. */
  net: number;
  /** Store net ÷ store transactions. */
  avgBasket: number | null;
  /** Store net ÷ entries, over matched days. */
  salesPerVisitor: number | null;
  /** Delivery net ÷ (store + delivery net). */
  deliveryShare: number | null;
  /** |Return net| ÷ (store + delivery net). */
  returnRate: number | null;
}

/** Totals over `days` for `branchIds` (null = every branch present). */
export function rangeMetrics(
  days: string[],
  footfall: BranchDailyPoint[],
  sales: SalesDailyPoint[],
  branchIds: string[] | null,
): RangeMetrics {
  const inRange = new Set(days);
  const ids = branchIds ? new Set(branchIds) : null;
  const foot = footfall.filter((r) => inRange.has(r.day) && (!ids || ids.has(r.branch_id)));
  const sold = sales.filter((r) => inRange.has(r.day) && (!ids || ids.has(r.branch_id)));
  const footByKey = new Map(foot.map((r) => [key(r.branch_id, r.day), r]));

  const m: RangeMetrics = {
    entries: 0, exits: 0, footfallDays: 0, salesDays: 0, matchedDays: 0,
    matchedEntries: 0, matchedTransactions: 0, matchedStoreNet: 0, conversion: null,
    storeTransactions: 0, storeNet: 0, deliveryTransactions: 0, deliveryNet: 0,
    returnTransactions: 0, returnNet: 0, discount: 0, net: 0,
    avgBasket: null, salesPerVisitor: null, deliveryShare: null, returnRate: null,
  };
  const footfallDays = new Set<string>();
  const salesDays = new Set<string>();
  const matchedDays = new Set<string>();
  for (const r of foot) {
    m.entries += r.entries;
    m.exits += r.exits;
    footfallDays.add(r.day);
  }
  for (const s of sold) {
    salesDays.add(s.day);
    m.storeTransactions += s.store_transactions;
    m.storeNet += s.store_net;
    m.deliveryTransactions += s.delivery_transactions;
    m.deliveryNet += s.delivery_net;
    m.returnTransactions += s.return_transactions;
    m.returnNet += s.return_net;
    m.discount += s.discount;
    m.net += s.net;
    const f = footByKey.get(key(s.branch_id, s.day));
    if (f && f.entries > 0) {
      matchedDays.add(s.day);
      m.matchedEntries += f.entries;
      m.matchedTransactions += s.store_transactions;
      m.matchedStoreNet += s.store_net;
    }
  }
  m.footfallDays = footfallDays.size;
  m.salesDays = salesDays.size;
  m.matchedDays = matchedDays.size;
  m.conversion = conversionRate(m.matchedTransactions, m.matchedEntries);
  m.avgBasket = m.storeTransactions > 0 ? m.storeNet / m.storeTransactions : null;
  m.salesPerVisitor = m.matchedEntries > 0 ? m.matchedStoreNet / m.matchedEntries : null;
  const gross = m.storeNet + m.deliveryNet;
  m.deliveryShare = gross > 0 ? m.deliveryNet / gross : null;
  m.returnRate = gross > 0 ? Math.abs(m.returnNet) / gross : null;
  return m;
}

/** Relative change, `current` against `previous`; null without a usable
 *  previous value. */
export function relativeDelta(
  current: number | null,
  previous: number | null,
): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

/** Change in a rate, in fraction points (0.02 = +2 percentage points). */
export function pointDelta(
  current: number | null,
  previous: number | null,
): number | null {
  if (current === null || previous === null) return null;
  return current - previous;
}

/** Egypt's week starts on Saturday. */
export const WEEK_ORDER = [6, 0, 1, 2, 3, 4, 5];

export interface WeekdayPoint {
  weekday: number;
  days: number;
  avgEntries: number | null;
  avgConversion: number | null;
}

/** Average entries and conversion per weekday over the rows that have
 *  them — which days of the week carry the store. */
export function weekdayProfile(rows: DayRow[]): WeekdayPoint[] {
  const entries = new Map<number, number[]>();
  const conversion = new Map<number, number[]>();
  for (const row of rows) {
    const weekday = weekdayOf(row.day);
    if (row.entries !== null) entries.set(weekday, [...(entries.get(weekday) ?? []), row.entries]);
    if (row.conversion !== null)
      conversion.set(weekday, [...(conversion.get(weekday) ?? []), row.conversion]);
  }
  const mean = (values: number[] | undefined) =>
    values && values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
  return WEEK_ORDER.map((weekday) => ({
    weekday,
    days: entries.get(weekday)?.length ?? 0,
    avgEntries: mean(entries.get(weekday)),
    avgConversion: mean(conversion.get(weekday)),
  }));
}
